import type { ShipTime } from '@felix/domain';
import { type ReactNode, useState } from 'react';
import { useShell } from '../../shell-context.js';
import { purchaseOrderPath } from './orders.js';
import { RequisitionDetailView } from './RequisitionDetailView.js';
import { RequisitionForm } from './RequisitionForm.js';
import { RequisitionList } from './RequisitionList.js';
import { REQUISITIONS_PATH, type RequisitionRoute, requisitionPath } from './requisitions.js';
import { rfqPath } from './rfqs.js';
import type { PurchasingSource } from './source.js';
import type { RequisitionDetail } from './types.js';

export interface RequisitionsScreenProps {
  source: PurchasingSource;
  /** Sorgu dizesinden (`?new=1`, `?id=…`, `?id=…&edit=1`; `requisitionRoute`). */
  route: RequisitionRoute;
  /** Tarih-saatin saat dilimi (gemide geminin). */
  /** Gemi saati: dilim ya da saat kaydının çizelgesi (ADR 0065 §4). */
  timeZone?: ShipTime;
  extraActions?: (detail: RequisitionDetail) => ReactNode;
}

/**
 * Material Requisition (M12b Task 11–12; `satin-alma-akisi.jpeg`): liste, yeni ya da düzeltilen
 * talep (yalnız gemide) ve talebin onay akışı. Görünüm yolun sorgu dizesinden gelir; ekran
 * gemi uygulamasında ve web ofiste aynıdır (ADR 0019 §2).
 */
export function RequisitionsScreen({
  source,
  route,
  timeZone,
  extraActions,
}: RequisitionsScreenProps) {
  const shell = useShell();
  const [message, setMessage] = useState<string | null>(null);
  const go = (next: RequisitionRoute, notice: string | null = null) => {
    setMessage(notice);
    shell?.navigate(requisitionPath(next));
  };
  const editor = source.editor;

  if ((route.view === 'new' || route.view === 'edit') && editor) {
    return (
      <RequisitionForm
        key={
          route.view === 'edit'
            ? route.id
            : `new-${route.from ?? ''}-${route.link?.defectId ?? route.link?.hseReportId ?? ''}-${route.department ?? ''}`
        }
        source={source}
        editor={editor}
        requisitionId={route.view === 'edit' ? route.id : null}
        shortfallOf={route.view === 'new' ? (route.from ?? null) : null}
        link={route.view === 'new' ? (route.link ?? null) : null}
        department={route.view === 'new' ? (route.department ?? null) : null}
        onDone={(id, notice) => go({ view: 'detail', id }, notice)}
        onCancel={() =>
          go(route.view === 'edit' ? { view: 'detail', id: route.id } : { view: 'list' })
        }
      />
    );
  }
  if (route.view === 'detail' || route.view === 'edit') {
    return (
      <RequisitionDetailView
        key={route.id}
        source={source}
        id={route.id}
        message={message}
        onEdit={() => go({ view: 'edit', id: route.id })}
        onBack={() => go({ view: 'list' })}
        {...(timeZone ? { timeZone } : {})}
        {...(extraActions ? { extraActions } : {})}
        onOpenRfq={(rfqId) =>
          shell?.navigate(rfqPath({ view: 'detail', id: rfqId, tab: 'details' }))
        }
        onOpenOrder={(orderId) =>
          shell?.navigate(purchaseOrderPath({ view: 'detail', id: orderId, tab: 'items' }))
        }
        onShortfall={() => go({ view: 'new', from: route.id })}
      />
    );
  }
  return (
    <RequisitionList
      source={source}
      onOpen={(id) => go({ view: 'detail', id })}
      onNew={() => go({ view: 'new' })}
    />
  );
}

export { REQUISITIONS_PATH };
